// The animated favicon of Music24 (owner request 2026-10-29). The four bars of the emblem move like
// an equalizer and settle back into the 4: the motion of the animated app icon GIF
// (scripts/music24-brand/build_brand.py `dance_spans`). A browser shows a GIF favicon still, so the
// player swaps the `href` of the SVG favicon link about 10 times a second. music24.com plays it once
// at page load; the web app plays it while a kit asset or an AI reel is being made.
//
// No imports: music24-website keeps a copy (src/components/favicon/faviconMotion.ts) that its
// test pins to this file.

/** The colours of the four bars, left to right (assets/music24-brand/README.md). */
export const FAVICON_BAR_COLOURS = ['#FF0A57', '#C912A8', '#9515F5', '#6A17FB'] as const;

/** One bar: its top and bottom in parts of the emblem height. */
export type FaviconBarSpan = readonly [top: number, bottom: number];

/** The bars of the still emblem (`BARS` of build_brand.py). Bar 3 is the stem. */
export const FAVICON_BAR_SPANS: readonly FaviconBarSpan[] = [
  [0.52, 0.76],
  [0.2, 0.76],
  [0, 1],
  [0.52, 0.76],
];

/** Bar width and the step from bar to bar, in parts of the emblem height (`BAR_W`, `BAR_STEP`). */
const BAR_WIDTH = 0.189;
const BAR_STEP = 0.232;
/** The emblem box of the favicon SVG (assets/music24-brand/svg/emblem.svg). */
const BOX = 1000;

/** The time between two frames. A background tab slows it to about one frame a second. */
export const FAVICON_FRAME_MS = 100;
/** The time the bars take to start moving and to settle back into the 4. */
export const FAVICON_EASE_SEC = 0.35;
/** The length of the dance that plays once (music24.com). */
export const FAVICON_ONCE_SEC = 2.2;

const smooth = (value: number): number => {
  const a = Math.max(0, Math.min(1, value));
  return a * a * (3 - 2 * a);
};

/**
 * The bars `tSec` seconds into the dance. `mix` 0 gives the still emblem, 1 the full dance. Each bar
 * keeps its foot and moves its top on its own rhythm, between a round dot and the full height.
 */
export function faviconBarSpans(tSec: number, mix: number): FaviconBarSpan[] {
  return FAVICON_BAR_SPANS.map(([top, bottom], i) => {
    const level =
      0.5 +
      0.5 *
        Math.sin(2 * Math.PI * (1.6 + 0.37 * i) * tSec + i * 1.3) *
        Math.sin(2 * Math.PI * (0.9 + 0.21 * i) * tSec + i);
    const danceHeight = BAR_WIDTH + level * (1 - BAR_WIDTH);
    return [top - mix * (danceHeight - (bottom - top)), bottom] as const;
  });
}

/** The mix of a dance that plays once for `durationSec`: it eases in and settles back at the end. */
export function faviconOnceMix(tSec: number, durationSec: number = FAVICON_ONCE_SEC): number {
  return smooth(tSec / FAVICON_EASE_SEC) * (1 - smooth((tSec - durationSec + FAVICON_EASE_SEC) / FAVICON_EASE_SEC));
}

const fmt = (value: number): string => {
  if (Math.abs(value) < 1e-9) {
    return '0';
  }
  return value.toFixed(2).replace(/\.?0+$/, '');
};

/** A round-ended bar from `top` to `bottom` (`pill` of build_brand.py). */
function pillPath(x: number, top: number, width: number, bottom: number): string {
  const r = width / 2;
  if (bottom - top <= width) {
    const cy = (top + bottom) / 2;
    return `M${fmt(x)} ${fmt(cy)}a${fmt(r)} ${fmt(r)} 0 1 0 ${fmt(width)} 0a${fmt(r)} ${fmt(r)} 0 1 0 ${fmt(-width)} 0Z`;
  }
  return (
    `M${fmt(x)} ${fmt(top + r)}a${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(width)} 0V${fmt(bottom - r)}` +
    `a${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(-width)} 0Z`
  );
}

/** One frame as an SVG document in the box of the favicon SVG. */
export function faviconFrameSvg(spans: readonly FaviconBarSpan[]): string {
  const width = BAR_WIDTH * BOX;
  const step = BAR_STEP * BOX;
  const left = (BOX - (3 * step + width)) / 2;
  const paths = spans
    .map(
      ([top, bottom], i) =>
        `<path fill="${FAVICON_BAR_COLOURS[i]}" d="${pillPath(left + i * step, top * BOX, width, bottom * BOX)}"/>`
    )
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BOX} ${BOX}">${paths}</svg>`;
}

/** One frame as a `data:` address for the `href` of the favicon link. */
export function faviconFrameHref(spans: readonly FaviconBarSpan[]): string {
  return `data:image/svg+xml,${encodeURIComponent(faviconFrameSvg(spans))}`;
}

/** The part of `document` that the player uses. */
export interface FaviconDocument {
  querySelector(
    selector: string
  ): { getAttribute(name: string): string | null; setAttribute(name: string, value: string): void } | null;
  visibilityState?: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface FaviconLoop {
  /** The bars settle back into the 4, then the favicon link gets its own file again. */
  stop(): void;
}

const SVG_ICON_SELECTOR = 'link[rel~="icon"][type="image/svg+xml"]';

/** True when the person asked the system for less motion. The player never runs then. */
export function prefersReducedMotion(): boolean {
  const { matchMedia } = globalThis as { matchMedia?: (query: string) => { matches: boolean } };
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The end of the dance that plays now. One dance at a time: a new one ends the old one first. */
let endActive: (() => void) | null = null;

/**
 * Plays frames on the SVG favicon link. `mixAt` gives the mix for the seconds since the start, or
 * null when the dance is over. A page with no SVG favicon link plays nothing.
 */
function play(doc: FaviconDocument, mixAt: (tSec: number) => number | null): () => void {
  endActive?.();
  const link = doc.querySelector(SVG_ICON_SELECTOR);
  if (link === null) {
    return () => undefined;
  }
  const original = link.getAttribute('href');
  const startedAt = Date.now();
  let timer: ReturnType<typeof setInterval> | null = null;
  const end = (): void => {
    if (timer === null) {
      return;
    }
    clearInterval(timer);
    timer = null;
    if (endActive === end) {
      endActive = null;
    }
    if (original !== null) {
      link.setAttribute('href', original);
    }
  };
  endActive = end;
  const frame = (): void => {
    const tSec = (Date.now() - startedAt) / 1000;
    const mix = mixAt(tSec);
    if (mix === null) {
      end();
      return;
    }
    link.setAttribute('href', faviconFrameHref(faviconBarSpans(tSec, mix)));
  };
  timer = setInterval(frame, FAVICON_FRAME_MS);
  frame();
  return end;
}

/**
 * Plays the dance once (music24.com, at page load). A hidden tab waits until the person sees it.
 * Returns a function that stops the dance and puts the favicon back.
 */
export function playFaviconOnce(doc: FaviconDocument, durationSec: number = FAVICON_ONCE_SEC): () => void {
  let stopPlay: (() => void) | null = null;
  const start = (): void => {
    stopPlay = play(doc, (tSec) => (tSec >= durationSec ? null : faviconOnceMix(tSec, durationSec)));
  };
  if (doc.visibilityState !== 'hidden') {
    start();
    return () => stopPlay?.();
  }
  const onVisible = (): void => {
    if (doc.visibilityState === 'hidden') {
      return;
    }
    doc.removeEventListener('visibilitychange', onVisible);
    start();
  };
  doc.addEventListener('visibilitychange', onVisible);
  return () => {
    doc.removeEventListener('visibilitychange', onVisible);
    stopPlay?.();
  };
}

/** Starts the dance until `stop()` (the web app, while something is being made). */
export function startFaviconLoop(doc: FaviconDocument): FaviconLoop {
  let stoppedAt: number | null = null;
  let mixAtStop = 0;
  const startedAt = Date.now();
  play(doc, (tSec) => {
    if (stoppedAt === null) {
      mixAtStop = smooth(tSec / FAVICON_EASE_SEC);
      return mixAtStop;
    }
    const settled = (tSec - stoppedAt) / FAVICON_EASE_SEC;
    return settled >= 1 ? null : mixAtStop * (1 - smooth(settled));
  });
  return {
    stop: () => {
      if (stoppedAt === null) {
        stoppedAt = (Date.now() - startedAt) / 1000;
      }
    },
  };
}
